import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertTriangle, RotateCcw, Plus } from 'lucide-react';
import { useRestoreSnapshot, useListStorageClasses } from '@/service/gpu-instance/volume-hook';
import type { VolumeSnapshot } from '@/service/gpu-instance/volume-type';
import { toast } from 'sonner';

interface RestoreSnapshotDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  snapshot: VolumeSnapshot | null;
  onSuccess?: () => void;
}

type RestoreType = 'in-place' | 'new-volume';

export const RestoreSnapshotDialog = ({
  open,
  onOpenChange,
  snapshot,
  onSuccess,
}: RestoreSnapshotDialogProps) => {
  const [restoreType, setRestoreType] = useState<RestoreType>('new-volume');
  const [newVolumeName, setNewVolumeName] = useState('');
  const [storageClassId, setStorageClassId] = useState<string>('');

  const restoreSnapshot = useRestoreSnapshot();
  const { data: storageClasses = [] } = useListStorageClasses();

  if (!snapshot) return null;

  const validateName = (value: string) => {
    return /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/.test(value);
  };

  const isValid =
    restoreType === 'in-place' ||
    (restoreType === 'new-volume' && newVolumeName.length >= 1 && validateName(newVolumeName));

  const handleRestore = async () => {
    if (!isValid) return;

    try {
      await restoreSnapshot.mutateAsync({
        snapshotId: snapshot.id,
        restoreType,
        newVolumeName: restoreType === 'new-volume' ? newVolumeName : undefined,
        newStorageClassId: storageClassId || undefined,
      });

      if (restoreType === 'in-place') {
        toast.success('Restore initiated', {
          description: `Restoring volume "${snapshot.volumeName}" to snapshot "${snapshot.name}".`,
        });
      } else {
        toast.success('Volume creation initiated', {
          description: `Creating new volume "${newVolumeName}" from snapshot "${snapshot.name}".`,
        });
      }

      onOpenChange(false);
      onSuccess?.();
      // Reset form
      setRestoreType('new-volume');
      setNewVolumeName('');
      setStorageClassId('');
    } catch {
      toast.error('Failed to restore snapshot');
    }
  };

  const suggestedName = `${snapshot.volumeName}-restored`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5" />
            Restore Snapshot
          </DialogTitle>
          <DialogDescription>
            Restore from snapshot "{snapshot.name}" created on{' '}
            {new Date(snapshot.createdAt).toLocaleString()}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Snapshot Info */}
          <div className="rounded-lg bg-muted/50 p-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Snapshot</span>
              <span className="font-medium">{snapshot.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Source Volume</span>
              <span>{snapshot.volumeName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Size</span>
              <span>{snapshot.size} GB</span>
            </div>
            {snapshot.description && (
              <div className="pt-2 border-t">
                <span className="text-muted-foreground">Description:</span>
                <p className="mt-1">{snapshot.description}</p>
              </div>
            )}
          </div>

          {/* Restore Type */}
          <div className="space-y-3">
            <Label>Restore Method</Label>
            <RadioGroup
              value={restoreType}
              onValueChange={(v) => setRestoreType(v as RestoreType)}
              className="space-y-3"
            >
              <label
                className={`flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors hover:bg-accent ${
                  restoreType === 'new-volume' ? 'border-primary bg-accent' : ''
                }`}
              >
                <RadioGroupItem value="new-volume" className="mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Plus className="h-4 w-4" />
                    <span className="font-medium">Create New Volume</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Create a new volume from this snapshot. Original volume is unchanged.
                  </p>
                </div>
              </label>

              <label
                className={`flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors hover:bg-accent ${
                  restoreType === 'in-place' ? 'border-primary bg-accent' : ''
                }`}
              >
                <RadioGroupItem value="in-place" className="mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <RotateCcw className="h-4 w-4" />
                    <span className="font-medium">In-Place Restore (TASR)</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Replace current volume data with snapshot. Volume must be detached.
                  </p>
                </div>
              </label>
            </RadioGroup>
          </div>

          {/* In-place warning */}
          {restoreType === 'in-place' && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                This will overwrite all current data in "{snapshot.volumeName}" with the snapshot
                data. This action cannot be undone.
              </AlertDescription>
            </Alert>
          )}

          {/* New volume options */}
          {restoreType === 'new-volume' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="new-volume-name">New Volume Name</Label>
                <Input
                  id="new-volume-name"
                  placeholder={suggestedName}
                  value={newVolumeName}
                  onChange={(e) => setNewVolumeName(e.target.value.toLowerCase())}
                  className={
                    newVolumeName && !validateName(newVolumeName) ? 'border-destructive' : ''
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="storage-class">Storage Class (optional)</Label>
                <Select value={storageClassId} onValueChange={setStorageClassId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Same as original volume" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Same as original volume</SelectItem>
                    {storageClasses.map((sc) => (
                      <SelectItem key={sc.id} value={sc.id}>
                        {sc.displayName} - ${sc.pricePerGBMonth}/GB/mo
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleRestore}
            disabled={!isValid || restoreSnapshot.isPending}
            variant={restoreType === 'in-place' ? 'destructive' : 'default'}
          >
            {restoreSnapshot.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {restoreType === 'in-place' ? 'Restore In-Place' : 'Create Volume'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RestoreSnapshotDialog;
